import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AffectedPolicy,
    getAffectedPolicies,
} from '../../../services/api/departmentVariablesService';
import LoadingRing from '../../ui-components/LoadingRing';

interface AffectedPoliciesListProps {
    variableName: string;
    onLoaded?: (policies: AffectedPolicy[]) => void;
}

export function AffectedPoliciesList({
    variableName,
    onLoaded,
}: AffectedPoliciesListProps) {
    const { t } = useTranslation();
    const [policies, setPolicies] = useState<AffectedPolicy[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        getAffectedPolicies(variableName)
            .then((data) => {
                if (cancelled) return;
                setPolicies(data);
                onLoaded?.(data);
            })
            .catch(() => {
                if (!cancelled) setPolicies([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [variableName]);

    if (loading) {
        return (
            <div className="flex justify-center py-2">
                <LoadingRing size="small" />
            </div>
        );
    }

    if (policies.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {t('departmentVariables.affectedPolicies.noPolicies')}
            </p>
        );
    }

    return (
        <div className="mt-2">
            <p className="text-sm font-medium mb-1.5">
                {policies.length === 1
                    ? t('departmentVariables.affectedPolicies.countOne')
                    : t('departmentVariables.affectedPolicies.countOther', { count: policies.length })}
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                {policies.map((p) => (
                    <li key={p._id} className="flex items-center gap-2">
                        <span className="truncate">
                            {p.title || t('departmentVariables.affectedPolicies.untitledPolicy')}
                        </span>
                        {p.status && (
                            <span className="text-xs text-muted-foreground/60 shrink-0">
                                ({p.status})
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
